import { Link, useSearchParams } from 'react-router-dom';

interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ page, totalPages, onPageChange }: PaginationProps) {
  const [searchParams] = useSearchParams();

  if (totalPages <= 1) return null;

  const pages: (number | string)[] = [];
  const showEllipsisStart = page > 3;
  const showEllipsisEnd = page < totalPages - 2;

  if (showEllipsisStart) {
    pages.push(1);
    if (page > 4) pages.push('...');
  }

  for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) {
    if (!pages.includes(i)) {
      pages.push(i);
    }
  }

  if (showEllipsisEnd) {
    if (page < totalPages - 3) pages.push('...');
    if (!pages.includes(totalPages)) pages.push(totalPages);
  }

  // Build URL for a given page number
  const getPageUrl = (pageNum: number) => {
    const params = new URLSearchParams(searchParams);
    if (pageNum === 1) {
      params.delete('page');
    } else {
      params.set('page', String(pageNum));
    }
    const queryString = params.toString();
    return queryString ? `/?${queryString}` : '/';
  };

  const handleClick = (e: React.MouseEvent, pageNum: number) => {
    e.preventDefault();
    onPageChange(pageNum);
  };

  return (
    <nav className="flex items-center justify-center gap-1">
      {page === 1 ? (
        <span className="btn-ghost px-3 py-2 opacity-50 cursor-not-allowed">
          Previous
        </span>
      ) : (
        <Link
          to={getPageUrl(page - 1)}
          onClick={(e) => handleClick(e, page - 1)}
          className="btn-ghost px-3 py-2"
        >
          Previous
        </Link>
      )}

      {pages.map((p, i) =>
        typeof p === 'number' ? (
          <Link
            key={i}
            to={getPageUrl(p)}
            onClick={(e) => handleClick(e, p)}
            className={`btn px-3 py-2 min-w-[40px] ${
              p === page
                ? 'bg-primary-600 text-white pointer-events-none'
                : 'btn-ghost'
            }`}
          >
            {p}
          </Link>
        ) : (
          <span key={i} className="px-2 text-gray-400">
            {p}
          </span>
        )
      )}

      {page === totalPages ? (
        <span className="btn-ghost px-3 py-2 opacity-50 cursor-not-allowed">
          Next
        </span>
      ) : (
        <Link
          to={getPageUrl(page + 1)}
          onClick={(e) => handleClick(e, page + 1)}
          className="btn-ghost px-3 py-2"
        >
          Next
        </Link>
      )}
    </nav>
  );
}
